import { useState } from 'react';
import config from '../../utils/apiUrl';
import useSendThankYouEmail from '../email/useSendThankYouEmail';
import useCreateRecurrence from './useCreateRecurrence';
import useCreateTransaction from './useCreateTransaction';
import useEpaycoCardTransaction from './useEpaycoCardTransaction';
import useEpaycoPseTransaction from './useEpaycoPseTransaction';

const usePaymentProcessor = () => {
  const { processTransaction: processCardTransaction } =
    useEpaycoCardTransaction();
  const { processTransaction: processPseTransaction } =
    useEpaycoPseTransaction();

  const { createTransaction, loading: transactionLoading } =
    useCreateTransaction();
  const { createRecurrence, loading: recurrenceLoading } =
    useCreateRecurrence();

  const {
    sendEmail,
  } = useSendThankYouEmail();

  const [loading, setLoading] = useState(false);
  const [transactionState, setTransactionState] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const processPayment = async (paymentMethod, paymentData) => {
    setLoading(true);
    setTransactionState('processing');
    setErrorMessage('');
    setSuccessMessage('');
    console.log(paymentData);

    try {
      const paymentType =
        paymentData.recurrence === 'Solo una vez'
          ? 'ONE_TIME_DONATION'
          : 'RECURRING_PAYMENT';
      const creditCardMask = `${paymentData.cardNumber.slice(0, 4)}${'*'.repeat(
        8
      )}${paymentData.cardNumber.slice(-4)}`;
      const initialTransaction = {
        userId: paymentData.userId,
        foundationId: paymentData.foundation,
        campaignId: paymentData.campaign || '',
        amount: paymentData.amount,
        paymentMethod,
        paymentType,
        currency: 'COP',
        status: 'PENDING',
        creditCardMask,
        creditCardBrand: paymentData.cardBrand || '',
        company: paymentData.company,
      };

      const createdTransaction = await createTransaction(initialTransaction);

      let paymentResult;
      if (paymentMethod === 'CARD') {
        paymentData.extra1 = createdTransaction;
        paymentResult = await processCardTransaction(paymentData);
      } else if (paymentMethod === 'PSE') {
        paymentData.extra1 = createdTransaction;
        paymentResult = await processPseTransaction(paymentData);
        await updateTransactionStatus(createdTransaction, '', 'PENDING');
        setTransactionState('pse_pending');
        setLoading(false);
        return paymentResult;
      }

      const transactionStatus =
        paymentResult?.data?.transaction?.data?.estado === 'Aceptada'
          ? 'APPROVED'
          : 'DECLINED';

      if (transactionStatus === 'APPROVED') {
        setTransactionState('accepted');
        setSuccessMessage('¡Transacción exitosa! Gracias por tu donación.');

        const emailData = {
          email: paymentData.email,
          template: 'agradecimiento-titan',
          mergeVars: {
            username: paymentData.firstName,
            Foundationname: paymentData.foundationName,
            Amount: paymentData.amount,
            Type: paymentData.recurrence,
          },
          copyEmail: paymentData.copyEmail || '',
        };

        await sendEmail(emailData);
      } else {
        setTransactionState('rejected');
        const errorMsg =
          paymentResult?.data?.error?.description ||
          paymentResult?.data?.transaction?.data?.respuesta ||
          'Transacción rechazada.';
        setErrorMessage(errorMsg);

/*         // Enviar correo para notificar rechazo
        const rejectionEmailData = {
          email: paymentData.email,
          template: 'agradecimiento-titan',
          mergeVars: {
            username: paymentData.firstName,
            Foundationname: paymentData.foundationName,
            Amount: paymentData.amount,
            Type: paymentData.recurrence,
          },
          copyEmail: paymentData.copyEmail || '',
        };

        await sendEmail(rejectionEmailData); */
      }

      await updateTransactionStatus(
        createdTransaction,
        paymentResult?.data?.transaction?.data?.franquicia,
        transactionStatus
      );

      if (paymentType === 'RECURRING_PAYMENT') {
        const initialRecurrence = {
          userId: paymentData.userId,
          foundationId: paymentData.foundation,
          amount: paymentData.amount,
          creditCardMask,
          campaignId: paymentData.campaign,
          creditCardBrand: paymentData.cardBrand || '',
          company: 'titan',
        };
        const createdRecurrence = await createRecurrence(initialRecurrence);
        await updateRecurringPayment(
          createdRecurrence,
          transactionStatus,
          paymentResult?.data?.tokenCard?.cardTokenId
        );
      }

      return paymentResult;
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateTransactionStatus = async (transactionId, brand, status) => {
    const endpoint = '/company/updateTransaction';
    const transactionUpdateData = {
      transactionId,
      status: status || 'DECLINED',
      creditCardBrand: brand || '',
    };

    try {
      const response = await fetch(`${config.apiUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transactionUpdateData),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error('Error actualizando la transacción en DB.');
      }
    } catch (error) {
      handleError(error);
    }
  };

  const updateRecurringPayment = async (recurringPaymentId, status, token) => {
    const endpoint = '/companyRecurringPayments/update';
    const recurringPaymentUpdateData = {
      recurringPaymentId,
      status: status === 'DECLINED' ? 'DELETED' : 'ACTIVE',
      token,
    };
    try {
      const response = await fetch(`${config.apiUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(recurringPaymentUpdateData),
      });

      const data = await response.json();
      if (!data.success) {
        throw new Error('Error actualizando el pago recurrente en DB.');
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleError = (error) => {
    setTransactionState('error');
    setErrorMessage(error.message || 'Error al procesar la transacción.');
  };

  const resetTransactionState = () => {
    setTransactionState('');
    setErrorMessage('');
    setSuccessMessage('');
  };

  return {
    processPayment,
    loading: loading || transactionLoading || recurrenceLoading,
    transactionState,
    errorMessage,
    successMessage,
    resetTransactionState,
  };
};

export default usePaymentProcessor;
